// @flow
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import * as React from 'react'
import Badge from 'ui/atoms/Badge'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import Checkbox from 'ui/Checkbox'
import { getFormattedDate } from 'utils/dates'

import useAuditLog from '../hooks'
import type { AuditLogProps } from '../types'

function AuditLogsTable(): React.Node {
  const textSize = useScreenSizeStyle({
    md: { small: true, xsmall: false },
    generic: { small: false, xsmall: true },
  })

  const maxWidthText = useScreenSizeStyle({
    md: '170px',
    generic: '110px',
  })

  const {
    auditLogs,
    auditLogsLoading,
    selectedAuditLogs,
    toggleSelectedAuditLog,
  } = useAuditLog()

  const isAuditLogSelected = React.useCallback(
    (auditLog: AuditLogProps): boolean => {
      return selectedAuditLogs.findIndex((r) => r?.uid === auditLog?.uid) !== -1
    },
    [selectedAuditLogs]
  )

  const getStatusBadge = React.useCallback(
    (
      status:
        | 'in_progress'
        | 'succeed'
        | 'failed'
        | 'restarted'
        | 'warning'
        | 'missing'
    ) => {
      if (status === 'succeed') return <Badge $success text={status} />
      if (status === 'in_progress') return <Badge text={status} />
      if (status === 'failed') return <Badge $error text={status} />
      return <Badge $warning text={status} />
    },
    []
  )

  const getPatientStatusBadge = React.useCallback(
    (status: 'none' | 'created' | 'updated' | 'provider_not_connected') => {
      if (status === 'created') return <Badge $success text={status} />
      if (status === 'updated') return <Badge $success text={status} />
      if (status === 'provider_not_connected')
        return <Badge $error text={status} />
      return <Badge $warning text={status} />
    },
    []
  )

  return (
    <Table>
      <Table.Head
        columns={[
          <Checkbox
            noMargin
            checked={
              selectedAuditLogs.length !== 0 &&
              selectedAuditLogs.length ===
                auditLogs.filter((auditLog) => auditLog.restarts === 0).length
            }
            onChange={() => {
              const allSelected =
                selectedAuditLogs.length ===
                auditLogs.filter((auditLog) => auditLog.restarts === 0).length
              if (allSelected) {
                auditLogs.forEach(
                  (auditLog) =>
                    auditLog.restarts === 0 && toggleSelectedAuditLog(auditLog)
                )
              } else {
                auditLogs.forEach((auditLog) => {
                  if (
                    !isAuditLogSelected(auditLog) &&
                    auditLog.restarts === 0
                  ) {
                    toggleSelectedAuditLog(auditLog)
                  }
                })
              }
            }}
          />,
          'CLIENT',
          'FORM',
          'DOCUMENT',
          'PACKET',
          'STATUS',
          'CLIENT STATUS',
          'IS FILE UPLOAD',
          'SENT AT',
        ]}
      />
      <Table.Body>
        {auditLogsLoading && (
          <Table.Row>
            <Table.Column colspan={8}>Loading...</Table.Column>
          </Table.Row>
        )}
        {!auditLogsLoading && auditLogs.length === 0 && (
          <Table.Row>
            <Table.Column colspan={8}>No Results Found</Table.Column>
          </Table.Row>
        )}
        {!auditLogsLoading &&
          auditLogs.map((auditLog) => (
            <Table.Row key={auditLog.id}>
              <Table.Column>
                <Checkbox
                  noMargin
                  willShowNotAllowedCursor
                  checked={isAuditLogSelected(auditLog)}
                  disabled={auditLog.restarts > 0}
                  onChange={() => toggleSelectedAuditLog(auditLog)}
                />
              </Table.Column>
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  {auditLog.customer_name}
                </Text>
              </Table.Column>
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  {auditLog.form_name}
                </Text>
              </Table.Column>
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  {auditLog.document_name}
                </Text>
              </Table.Column>
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  {auditLog.packet_name}
                </Text>
              </Table.Column>
              <Table.Column>{getStatusBadge(auditLog.status)}</Table.Column>
              <Table.Column>
                {getPatientStatusBadge(auditLog.patient_status)}
              </Table.Column>
              <Table.Column>
                {auditLog.is_file_upload ? 'Yes' : 'No'}
              </Table.Column>
              <Table.Column>
                {getFormattedDate(auditLog.created_at)}
              </Table.Column>
            </Table.Row>
          ))}
      </Table.Body>
    </Table>
  )
}

export default AuditLogsTable
